import { Action, createReducer, on } from '@ngrx/store';
import { ComplianceReportNewState } from '../state/compliance-report-new.state';
import {
    clearAvailableStatuses,
    clearCurrentContract,
    clearReadinessStatuses,
    clearModifiedRequirements,
    getCurrentContract,
    getAvailableStatusesFailure,
    getAvailableStatusesSuccess,
    getCurrentContractFailure,
    getCurrentContractSuccess,
    getReadinessStatusesFailure,
    getReadinessStatusesSuccess,
    markRequirementAsModified,
    setRequirementAsModified,
    setCandidateDocumentAsModified,
    updateComplianceSpecialistName,
    updateCredentialingSpecialistName,
    saveRequirementsNotPerformed,
    saveRequirementsFailure,
    saveRequirementsSuccess,
    saveRequirements,
    putRequirements,
    updateModifiedRequirement,
    updateCurrentComplianceReportTab,
    addSelectedCandidateDocument,
    removeSelectedCandidateDocument,
    clearSelectedCandidateDocuments,
    bulkUpdateSelectedCandidateDocuments,
    updateRequirementsForCurrentContractSuccess,
    getCurrentPermissionsSuccess,
    getCurrentPermissionsFailure,
    clearCurrentPermissions,
    updateComplianceReportNote,
    updateComplianceReportNoteSuccess,
    updateComplianceReportNoteFailure,
    getAutomatchData,
    getAutomatchDataSuccess,
    getAutomatchDataFailure,
    clearAutomatchData,
    updateModifiedRequirements,
    saveSelectedCandidateDocument,
    updateManualQualifications,
    getJobRequirementsChangesSuccess,
    getJobRequirementsChangesFailure,
    updateContractComplianceStatus,
    setSingleSignalAutomatchSuccess,
    markCandidateDocumentAsModified,
    qualifyRequirements,
    qualifyRequirementsSuccess,
    qualifyRequirementsFailure,
    updateComplianceNoteForRequirementSuccess,
    addToManuallySelectedCandidateDocuments,
    clearManuallySelectedCandidateDocuments,
    updateCurrentComplianceStatus,
    addIdToManuallyDeselectedCandidateDocuments,
    clearAllData,
    updateRequirementsForCurrentContract,
    updateRequirementsForCurrentContractFailure,
    automatchSignalReceived,
    updateAyaClearedStatusForCurrentContractSuccess,
    updateClientClearedStatusForCurrentContractSuccess,
    markAsReviewed,
    markAsReviewedSuccess,
    markAsReviewedFailure,
    processSpecialRules,
    processSpecialRulesSuccess,
    qualifyContract,
    getIntegrationStatuses,
    getIntegrationStatusesSuccess,
    getIntegrationStatusesFailure,
    getCandidateReviewAuditLogs,
    getCandidateReviewAuditLogsSuccess,
    getCandidateReviewAuditLogsFailure,
    getHealthIntegrationStatuses,
    getHealthIntegrationStatusesSuccess,
    getHealthIntegrationStatusesFailure,
    updateCurrentCredentialingStatus
} from '../actions/compliance-report-new.actions';
import { RequirementDto } from '../../api/models/requirement-dto.model';
import { ComplianceStatusEnum } from 'src/app/compliance/api/enums/compliance-status-enum.enum';
import { DocumentForComplianceReportDto } from 'src/app/compliance/api/models/document-for-compliance-report-dto.model';
import { SpecialRulesRequirementDto } from '../../api/models/special-rules-requirement-dto.model';

export const accountManagerDashboardFeatureKey = 'account-managers-dashboard';

export const initialState: ComplianceReportNewState = {
    error: null,
    loading: false,
    qualifyLoading: false,
    currentContract: null,
    availableStatuses: null,
    readinessStatuses: null,
    originalRequirements: [],
    modifiedRequirements: [],
    documentSelectionValue: [],
    saveInProgress: false,
    currentTabIndex: 0,
    selectedCandidateDocumentIds: [],
    currentPermissions: null,
    automatchData: null,
    automatchDataLoading: false,
    specialRulesRequirementsDataLoading: null,
    manualQualifications: null,
    jobRequirements: null,
    manuallySelectedCandidateDocuments: [],
    manuallyDeselectedCandidateDocuments: [],
    currentComplianceStatus: null,
    hasClearedToOrientStatus: { currentValue: false, previousValue: false },
    hasAllowedOrientationStatuses: { currentValue: false, previousValue: false },
    specialRulesRequirements: [],
    autosentEnvelope: null,
    integrationStatuses: {},
    integrationStatusesLoading: false,
    candidateReviewAuditLogs: [],
    candidateReviewAuditLogsLoading: false,
    healthIntegrationStatuses: [],
    healthIntegrationStatusesLoading: false,
    toggledRequiredStateRequirements: []
};

const complianceReportNewReducer = createReducer(
    initialState,
    // REQUEST REDUCERS
    on(getCurrentContract, (state) => ({
        ...state,
        loading: true
    })),
    on(processSpecialRules, (state) => ({
        ...state,
        loading: false,
        specialRulesRequirementsDataLoading: true
    })),
    on(qualifyRequirements, (state) => ({
        ...state,
        loading: true,
        qualifyLoading: true
    })),
    on(qualifyContract, (state) => ({
        ...state,
        loading: true,
        qualifyLoading: true
    })),
    on(saveRequirements, (state) => ({
        ...state,
        loading: true
    })),
    on(putRequirements, (state) => ({
        ...state,
        loading: true
    })),
    on(updateComplianceReportNote, (state) => ({
        ...state,
        loading: true
    })),
    on(getAutomatchData, (state, action) => ({
        ...state,
        automatchDataLoading: action.isQualified ? false : true
    })),
    on(getIntegrationStatuses, (state, action) => ({
        ...state,
        integrationStatusesLoading: true
    })),
    on(getCandidateReviewAuditLogs, (state) => ({
        ...state,
        candidateReviewAuditLogsLoading: true
    })),
    on(getHealthIntegrationStatuses, (state, action) => ({
        ...state,
        healthIntegrationStatusesLoading: true
    })),
    on(updateRequirementsForCurrentContract, (state) => ({
        ...state,
        currentContract: {
            ...state.currentContract,
            requirements: []
        },
        loading: true
    })),
    on(markAsReviewed, (state) => ({
        ...state,
        loading: true
    })),

    // SUCCESS REDUCERS
    on(getCurrentContractSuccess, (state, action) => ({
        ...state,
        currentContract: action.currentContract,
        originalRequirements: action.currentContract.requirements,
        automatchData: {
            ...state.automatchData,
            contractId: action.currentContract.contractId,
            documents: {}
        },
        loading: false,
        error: null
    })),
    on(processSpecialRulesSuccess, (state, action) => ({
        ...state,
        specialRulesRequirements: action.SpecialRulesRequirementDto,
        currentContract: {
            ...state.currentContract,
            requirements: applySpecialRules(state.currentContract.requirements, action.SpecialRulesRequirementDto)
        },
        modifiedRequirements: applySpecialRules(state.modifiedRequirements, action.SpecialRulesRequirementDto),
        specialRulesRequirementsDataLoading: false,
        loading: false,
        error: null
    })),

    on(updateAyaClearedStatusForCurrentContractSuccess, (state, action) => ({
        ...state,
        currentContract: {
            ...state.currentContract,
            isAyaCleared: action.status.ayaCleared
        },
        loading: false,
        error: null
    })),

    on(updateClientClearedStatusForCurrentContractSuccess, (state, action) => ({
        ...state,
        currentContract: {
            ...state.currentContract,
            isClientCleared: action.status.clientCleared
        },
        loading: false,
        error: null
    })),
    on(getCurrentPermissionsSuccess, (state, action) => ({
        ...state,
        currentPermissions: action.permissions,
        loading: false,
        error: null
    })),
    on(getAvailableStatusesSuccess, (state, action) => ({
        ...state,
        availableStatuses: action.availableStatuses,
        loading: false,
        error: null
    })),
    on(getReadinessStatusesSuccess, (state, action) => ({
        ...state,
        readinessStatuses: action.readinessStatuses,
        loading: false,
        error: null
    })),
    on(updateComplianceSpecialistName, (state, action) => ({
        ...state,
        currentContract: {
            ...state.currentContract,
            complianceLiaison: action.complianceSpecialistName
        }
    })),
    on(updateCredentialingSpecialistName, (state, action) => ({
        ...state,
        currentContract: {
            ...state.currentContract,
            credentialingSpecialist: action.credentialingSpecialistName
        }
    })),

    on(updateComplianceNoteForRequirementSuccess, (state, action) => ({
        ...state,
        currentContract: {
            ...state.currentContract,
            requirements: [
                ...state.currentContract.requirements.map((req) => {
                    if (
                        (action.requirementId && req.requirementId === action.requirementId) ||
                        req.documentTypeId === action.documentTypeId
                    ) {
                        return {
                            ...req,
                            complianceNote: action.savedComplianceNote.note,
                            complianceNoteUpdatedBy: action.savedComplianceNote.updatedBy,
                            complianceNoteUpdatedDate: action.savedComplianceNote.updated
                        };
                    }
                    return req;
                })
            ]
        }
    })),

    on(qualifyRequirementsSuccess, (state) => ({
        ...state,
        currentContract: {
            ...state.currentContract,
            isAudited: true
        },
        modifiedRequirements: [],
        manuallyDeselectedCandidateDocuments: [],
        manuallySelectedCandidateDocuments: [],
        selectedCandidateDocumentIds: [],
        manualQualifications: null,
        qualifyLoading: false
    })),
    on(saveRequirementsSuccess, (state) => ({
        ...state,
        modifiedRequirements: [],
        manuallyDeselectedCandidateDocuments: [],
        manuallySelectedCandidateDocuments: [],
        selectedCandidateDocumentIds: []
    })),
    on(updateRequirementsForCurrentContractSuccess, (state, action) => ({
        ...state,
        currentContract: {
            ...state.currentContract,
            requirements: action.requirements
        },
        loading: false
    })),
    on(updateComplianceReportNoteSuccess, (state, action) => ({
        ...state,
        currentContract: {
            ...state.currentContract,
            note: action.note
        },
        loading: false
    })),
    on(getAutomatchDataSuccess, (state, action) => ({
        ...state,
        automatchData: action.automatchData,
        automatchDataLoading: false
    })),
    on(getIntegrationStatusesSuccess, (state, action) => ({
        ...state,
        integrationStatuses: action.integrationStatuses,
        integrationStatusesLoading: false
    })),
    on(getIntegrationStatusesFailure, (state) => ({
        ...state,
        integrationStatusesLoading: false
    })),
    on(getCandidateReviewAuditLogsSuccess, (state, action) => ({
        ...state,
        candidateReviewAuditLogs: action.auditLogs,
        candidateReviewAuditLogsLoading: false
    })),
    on(getCandidateReviewAuditLogsFailure, (state) => ({
        ...state,
        candidateReviewAuditLogsLoading: false
    })),
    on(getHealthIntegrationStatusesSuccess, (state, action) => ({
        ...state,
        healthIntegrationStatuses: action.healthIntegrationStatuses,
        healthIntegrationStatusesLoading: false
    })),
    on(getHealthIntegrationStatusesFailure, (state) => ({
        ...state,
        healthIntegrationStatusesLoading: false
    })),
    on(getJobRequirementsChangesSuccess, (state, action) => ({
        ...state,
        jobRequirements: action.jobRequirements
    })),
    on(setSingleSignalAutomatchSuccess, (state, action) => {
        if (action.automatchData.contractId === state?.automatchData?.contractId) {
            return {
                ...state,
                automatchDataLoading: false,
                automatchData: {
                    ...state.automatchData,
                    documents: {
                        ...state.automatchData.documents,
                        [action.automatchData.documentTypeId]:
                            state.automatchData?.documents[action.automatchData?.documentTypeId]?.map((doc) => {
                                if (doc?.candidateDocumentId === action.automatchData.candidateDocumentId) {
                                    return {
                                        ...doc,
                                        autoMatchStatus: action.automatchData.autoMatchStatus,
                                        autoMatchFields: action.automatchData.fields
                                    };
                                }
                                return doc;
                            }) || []
                    }
                }
            };
        }

        return { ...state };
    }),
    on(markAsReviewedSuccess, (state, action) => ({
        ...state,
        currentContract: {
            ...state.currentContract,
            complianceStatus: action.complianceStatus.complianceStatusId
        },
        loading: false
    })),

    // STORE UPDATE REDUCERS
    on(updateCurrentComplianceReportTab, (state, action) => ({
        ...state,
        currentTabIndex: action.tab
    })),
    on(markRequirementAsModified, (state, action) => ({
        ...state,
        modifiedRequirements: getNewModifiedRequirementsFromStore(
            state.modifiedRequirements,
            action.requirement,
            action.isRequired
        )
    })),
    on(setRequirementAsModified, (state, action) => ({
        ...state,
        modifiedRequirements: getNewModifiedRequirementsFromStore(
            state.modifiedRequirements,
            action.requirement,
            action.isRequired
        ),
        toggledRequiredStateRequirements: [
            ...state.toggledRequiredStateRequirements.filter(
                (item) => item.documentTypeId !== action.requirement.documentTypeId
            ),
            { documentTypeId: action.requirement.documentTypeId, isRequired: action.isRequired }
        ],
        currentContract: {
            ...state.currentContract,
            requirements: state.currentContract.requirements.map((req) => {
                if (req.documentTypeId === action.requirement.documentTypeId) {
                    return {
                        ...req,
                        isRequired: action.isRequired
                    };
                }
                return req;
            })
        },
        documentSelectionValue: [
            ...state.documentSelectionValue.filter((item) => item.documentTypeId !== action.requirement.documentTypeId)
        ]
    })),
    on(setCandidateDocumentAsModified, (state, action) => {
        let newState = {
            ...state,
            modifiedRequirements: getModifiedCandidateDocumentsFromStore(
                state.modifiedRequirements,
                state.currentContract.requirements,
                action.requirement,
                action.candidateDocument,
                action.isSelected
            ),
            documentSelectionValue: [
                ...state.documentSelectionValue.filter(
                    (item) => item.documentTypeId !== action.requirement.documentTypeId
                ),
                {
                    documentTypeId: action.requirement.documentTypeId,
                    document: action.candidateDocument,
                    isSelected: action.isSelected
                }
            ]
        };

        if (action.isSelected && !action.requirement.isRequired) {
            newState = {
                ...newState,
                currentContract: {
                    ...state.currentContract,
                    requirements: state.currentContract.requirements.map((req) => {
                        if (req.documentTypeId === action.requirement.documentTypeId) {
                            return {
                                ...req,
                                isRequired: true
                            };
                        }
                        return req;
                    })
                }
            };
        }

        return newState;
    }),
    on(markCandidateDocumentAsModified, (state, action) => ({
        ...state,
        modifiedRequirements: getNewModifiedCandidateDocumentsFromStore(
            state.modifiedRequirements,
            state.currentContract.requirements,
            action.requirement,
            action.candidateDocument,
            action.isSelected
        ),
        currentContract: {
            ...state.currentContract,
            requirements: state.currentContract.requirements.map((req) => {
                if (req.documentTypeId === action.requirement.documentTypeId) {
                    return {
                        ...req,
                        documents: updateSelectedDocumentInRequirement(req, action.candidateDocument, action.isSelected)
                    };
                }
                return req;
            })
        },
        currentComplianceStatus: state.currentComplianceStatus,
        hasClearedToOrientStatus: {
            currentValue: hasClearedToOrientStatuses(state.currentComplianceStatus),
            previousValue: state.hasClearedToOrientStatus.currentValue
        },
        hasAllowedOrientationStatuses: {
            currentValue: hasAllowedOrientationStatuses(state.currentComplianceStatus),
            previousValue: state.hasAllowedOrientationStatuses.currentValue
        }
    })),
    on(saveSelectedCandidateDocument, (state, action) => ({
        ...state,
        currentContract: {
            ...state.currentContract,
            requirements: [
                ...state.currentContract.requirements.map((req) => {
                    if (action.isSelected && req.documentTypeId === action.requirement.documentTypeId) {
                        return {
                            ...req,
                            isRequired: true
                        };
                    }
                    return req;
                })
            ]
        }
    })),
    on(updateModifiedRequirement, (state, action) => ({
        ...state,
        modifiedRequirements: [
            ...state.modifiedRequirements.map((req) => {
                if (req.documentTypeId === action.requirement.documentTypeId) {
                    return action.requirement;
                }
                return req;
            })
        ]
    })),
    on(bulkUpdateSelectedCandidateDocuments, (state, action) => ({
        ...state,
        selectedCandidateDocumentIds: action.selectedCandidateDocumentIds
    })),
    on(addSelectedCandidateDocument, (state, action) => ({
        ...state,
        selectedCandidateDocumentIds: [...state.selectedCandidateDocumentIds, action.selectedCandidateDocumentId]
    })),
    on(removeSelectedCandidateDocument, (state, action) => ({
        ...state,
        selectedCandidateDocumentIds: state.selectedCandidateDocumentIds.filter(
            (id) => id !== action.selectedCandidateDocumentId
        )
    })),
    on(updateModifiedRequirements, (state, action) => ({
        ...state,
        modifiedRequirements: action.modifiedRequirements
    })),
    on(updateManualQualifications, (state, action) => ({
        ...state,
        manualQualifications: action.manualQualifications
    })),
    on(updateContractComplianceStatus, (state, action) => {
        if (state.currentContract.complianceStatus !== action.complianceStatus) {
            return {
                ...state,
                currentContract: {
                    ...state.currentContract,
                    complianceStatus: action.complianceStatus
                }
            };
        }
        return state;
    }),
    on(addToManuallySelectedCandidateDocuments, (state, action) => ({
        ...state,
        manuallySelectedCandidateDocuments: [
            ...state.manuallySelectedCandidateDocuments.filter(
                (object) => object.documentTypeId !== action.documentTypeId
            ),
            {
                document: action.candidateDocument,
                documentTypeId: action.documentTypeId
            }
        ],
        manuallyDeselectedCandidateDocuments: state.manuallyDeselectedCandidateDocuments.filter(
            (object) => object.documentTypeId !== action.documentTypeId
        )
    })),
    on(addIdToManuallyDeselectedCandidateDocuments, (state, action) => ({
        ...state,
        manuallyDeselectedCandidateDocuments: [
            ...state.manuallyDeselectedCandidateDocuments.filter(
                (object) => object.documentTypeId !== action.documentTypeId
            ),
            {
                document: action.candidateDocument,
                documentTypeId: action.documentTypeId
            }
        ],
        manuallySelectedCandidateDocumentIds: state.manuallySelectedCandidateDocuments.filter(
            (object) => object.documentTypeId !== action.documentTypeId
        )
    })),
    on(updateCurrentComplianceStatus, (state, action) => ({
        ...state,
        currentComplianceStatus: action.complianceStatus,
        hasClearedToOrientStatus: {
            currentValue: hasClearedToOrientStatuses(action.complianceStatus),
            previousValue: state.hasClearedToOrientStatus.currentValue
        },
        hasAllowedOrientationStatuses: {
            currentValue: hasAllowedOrientationStatuses(action.complianceStatus),
            previousValue: state.hasAllowedOrientationStatuses.currentValue
        }
    })),
    on(updateCurrentCredentialingStatus, (state, action) => ({
        ...state,
        currentCredentialingStatus: action.credentialingStatus
    })),
    on(automatchSignalReceived, (state, action) => ({
        ...state,
        automatchDataLoading: true
    })),

    // FAILURE REDUCERS
    on(getCurrentContractFailure, (state, action) => ({
        ...state,
        currentContract: null,
        loading: false,
        error: action.error
    })),
    on(getCurrentPermissionsFailure, (state, action) => ({
        ...state,
        currentPermissions: null,
        loading: false,
        error: action.error
    })),
    on(getAvailableStatusesFailure, (state, action) => ({
        ...state,
        availableStatuses: null,
        loading: false,
        error: action.error
    })),
    on(getReadinessStatusesFailure, (state, action) => ({
        ...state,
        readinessStatuses: null,
        loading: false,
        error: action.error
    })),
    on(qualifyRequirementsFailure, (state, action) => ({
        ...state,
        loading: false,
        qualifyLoading: false,
        error: action.error
    })),
    on(saveRequirementsFailure, (state, action) => ({
        ...state,
        loading: false,
        error: action.error
    })),
    on(saveRequirementsNotPerformed, (state) => ({
        ...state,
        loading: false
    })),
    on(updateComplianceReportNoteFailure, (state) => ({
        ...state,
        currentContract: {
            ...state.currentContract,
            complianceReportNote: null
        },
        loading: false
    })),
    on(getAutomatchDataFailure, (state, action) => ({
        ...state,
        automatchData: null,
        automatchDataLoading: false,
        error: action.error
    })),
    on(getJobRequirementsChangesFailure, (state, action) => ({
        ...state,
        error: action.error
    })),
    on(updateRequirementsForCurrentContractFailure, (state, action) => ({
        ...state,
        loading: false,
        error: action.error
    })),
    on(markAsReviewedFailure, (state, action) => ({
        ...state,
        loading: false,
        error: action.error
    })),

    // CLEAR STATE REDUCERS
    on(clearCurrentContract, (state) => ({
        ...state,
        currentContract: null,
        loading: false,
        error: null
    })),
    on(clearCurrentPermissions, (state) => ({
        ...state,
        currentPermissions: null,
        loading: false,
        error: null
    })),
    on(clearAvailableStatuses, (state) => ({
        ...state,
        availableStatuses: null,
        loading: false,
        error: null
    })),
    on(clearReadinessStatuses, (state) => ({
        ...state,
        readinessStatuses: null,
        loading: false,
        error: null
    })),
    on(clearModifiedRequirements, (state) => ({
        ...state,
        modifiedRequirements: []
    })),
    on(clearSelectedCandidateDocuments, (state) => ({
        ...state,
        selectedCandidateDocumentIds: []
    })),
    on(clearAutomatchData, (state) => ({
        ...state,
        automatchData: null,
        automatchDataLoading: false
    })),
    on(clearManuallySelectedCandidateDocuments, (state) => ({
        ...state,
        currentContract: {
            ...state.currentContract,
            requirements: []
        },
        automatchData: {
            ...state.automatchData,
            contractId: state.currentContract.contractId,
            documents: {}
        },
        originalRequirements: [],
        modifiedRequirements: [],
        manuallySelectedCandidateDocuments: [],
        manuallyDeselectedCandidateDocuments: [],
        documentSelectionValue: [],
        selectedCandidateDocumentIds: []
    })),
    on(clearAllData, (state) => ({
        ...state,
        error: null,
        loading: false,
        currentContract: null,
        availableStatuses: null,
        readinessStatuses: null,
        originalRequirements: [],
        modifiedRequirements: [],
        saveInProgress: false,
        currentTabIndex: 0,
        selectedCandidateDocumentIds: [],
        currentPermissions: null,
        automatchData: null,
        automatchDataLoading: false,
        manualQualifications: null,
        jobRequirements: null,
        manuallySelectedCandidateDocuments: [],
        manuallyDeselectedCandidateDocuments: [],
        currentComplianceStatus: null
    }))
);

export function getNewModifiedRequirementsFromStore(
    currentStoreModifiedRequirements: RequirementDto[],
    newRequirementFromAction: RequirementDto,
    isChecked: boolean
): RequirementDto[] {
    const newModifiedRequirement = { ...newRequirementFromAction, isRequired: isChecked };
    const modifiedRequirementFromAction = {
        ...newRequirementFromAction,
        isRequired: isChecked
    };

    if (!currentStoreModifiedRequirements || currentStoreModifiedRequirements?.length === 0) {
        return [modifiedRequirementFromAction];
    }

    if (
        currentStoreModifiedRequirements?.some(
            (req) => req?.documentTypeId === newRequirementFromAction?.documentTypeId
        )
    ) {
        // If the requirement is already in the modified requirements, update it
        return currentStoreModifiedRequirements.map((req) => {
            if (req?.documentTypeId === newRequirementFromAction?.documentTypeId) {
                return modifiedRequirementFromAction;
            }
            return req;
        });
    }

    // If the requirement is not in the modified requirements, add it
    return [...currentStoreModifiedRequirements, newModifiedRequirement];
}

export function getNewModifiedCandidateDocumentsFromStore(
    currentStoreModifiedRequirements: RequirementDto[],
    currentStoreExistingRequirements: RequirementDto[],
    requirementFromAction: RequirementDto,
    candidateDocumentFromAction: DocumentForComplianceReportDto,
    isSelected: boolean
): RequirementDto[] {
    // If the requirement is already in the modified requirements, update it
    if (
        currentStoreModifiedRequirements?.some((req) => req?.documentTypeId === requirementFromAction?.documentTypeId)
    ) {
        return currentStoreModifiedRequirements?.map((req) => {
            if (req?.documentTypeId === requirementFromAction?.documentTypeId) {
                return {
                    ...req,
                    documents: updateSelectedDocumentInRequirement(req, candidateDocumentFromAction, isSelected)
                };
            }

            return req;
        });
    }

    // If the requirement does not exist in the modified requirements, but does exist in the existing requirements, add it
    if (
        currentStoreExistingRequirements?.some((req) => req?.documentTypeId === requirementFromAction?.documentTypeId)
    ) {
        const requirementToModify = currentStoreExistingRequirements.find(
            (req) => req?.documentTypeId === requirementFromAction?.documentTypeId
        );
        return [
            ...currentStoreModifiedRequirements,
            {
                ...requirementToModify,
                documents: updateSelectedDocumentInRequirement(
                    requirementToModify,
                    candidateDocumentFromAction,
                    isSelected
                )
            }
        ];
    }

    return [];
}

export function getModifiedCandidateDocumentsFromStore(
    currentStoreModifiedRequirements: RequirementDto[],
    currentStoreExistingRequirements: RequirementDto[],
    requirementFromAction: RequirementDto,
    candidateDocumentFromAction: DocumentForComplianceReportDto,
    isSelected: boolean
): RequirementDto[] {
    // If the requirement is already in the modified requirements, update it
    if (
        currentStoreModifiedRequirements?.some((req) => req?.documentTypeId === requirementFromAction?.documentTypeId)
    ) {
        return currentStoreModifiedRequirements?.map((req) => {
            if (req?.documentTypeId === requirementFromAction?.documentTypeId) {
                return {
                    ...req,
                    isRequired: !req.isRequired && isSelected ? true : req.isRequired,
                    documents: updateSelectedDocumentInRequirement(req, candidateDocumentFromAction, isSelected)
                };
            }

            return req;
        });
    }

    // If the requirement does not exist in the modified requirements, but does exist in the existing requirements, add it
    if (
        currentStoreExistingRequirements?.some((req) => req?.documentTypeId === requirementFromAction?.documentTypeId)
    ) {
        const requirementToModify = currentStoreExistingRequirements.find(
            (req) => req?.documentTypeId === requirementFromAction?.documentTypeId
        );
        return [
            ...currentStoreModifiedRequirements,
            {
                ...requirementToModify,
                isRequired: !requirementToModify.isRequired && isSelected ? true : requirementToModify.isRequired,
                documents: updateSelectedDocumentInRequirement(
                    requirementToModify,
                    candidateDocumentFromAction,
                    isSelected
                )
            }
        ];
    }

    return [];
}

export function updateSelectedDocumentInRequirement(
    requirement: RequirementDto,
    selectedDocument: DocumentForComplianceReportDto,
    isSelected: boolean
): DocumentForComplianceReportDto[] {
    return [
        ...requirement.documents.map((doc) => {
            if (
                doc.candidateDocumentId === selectedDocument.candidateDocumentId &&
                doc.requirementId === selectedDocument.requirementId &&
                doc.documentId === selectedDocument.documentId
            ) {
                return {
                    ...doc,
                    isSelected: isSelected
                };
            }
            return {
                ...doc,
                isSelected: false
            };
        })
    ];
}

export function complianceReportReducerNew(state: ComplianceReportNewState | undefined, action: Action) {
    return complianceReportNewReducer(state, action);
}

export function hasClearedToOrientStatuses(complianceStatus: ComplianceStatusEnum): boolean {
    const clearedToOrientStatuses = [
        ComplianceStatusEnum.ClearedToStartPendingOrientation,
        ComplianceStatusEnum.CompliantPendingOrientation
    ];

    return clearedToOrientStatuses.includes(complianceStatus);
}

export function hasAllowedOrientationStatuses(complianceStatus: ComplianceStatusEnum): boolean {
    const allowedOrientationStatuses = [
        ComplianceStatusEnum.SentPartial,
        ComplianceStatusEnum.SentFull,
        ComplianceStatusEnum.ClientPendingItemS,
        ComplianceStatusEnum.Qualified
    ];

    return allowedOrientationStatuses.includes(complianceStatus);
}

export function showOrientationTab(
    hasScheduleWithAyaPersonnelPermission: boolean,
    complianceStatus?: ComplianceStatusEnum
): boolean {
    if (!complianceStatus) {
        return false;
    }

    if (
        hasScheduleWithAyaPersonnelPermission &&
        (hasClearedToOrientStatuses(complianceStatus) || hasAllowedOrientationStatuses(complianceStatus))
    ) {
        return true;
    }

    return false;
}

export function applySpecialRules(
    requirements: RequirementDto[],
    rules: SpecialRulesRequirementDto[]
): RequirementDto[] {
    return requirements.map((req) => {
        const specialRule = rules.find(
            (rule) =>
                (rule.requirementId !== null && rule.requirementId === req.requirementId) ||
                (rule.requirementId == null && rule.documentTypeId === req.documentTypeId)
        );

        if (specialRule) {
            return {
                ...req,
                isRequired: specialRule.isRequired,
                documents: req.documents.map((doc) => {
                    const specialRuleDoc = specialRule.documents.find(
                        (ruleDoc) =>
                            ruleDoc.candidateDocumentId === doc.candidateDocumentId &&
                            doc.requirementId === specialRule.requirementId
                    );
                    return specialRuleDoc ? { ...doc, isSelected: specialRuleDoc.isSelected } : doc;
                })
            };
        }

        return req;
    });
}
